import React from "react";
import {
  Text,
  Box,
  Flex,
  Collapse,
  useDisclosure,
  useTheme,
  Input,
} from "@chakra-ui/react";
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
} from "react-icons/md";
import { Theme } from "../theme";

type ZippyDropdownPropType = {
  id?: string;
  name?: string;
  optionHeight?: string;
  wrapperHeight?: string;
  width?: string;
  value?: any;
  disabled?: boolean;
  selectOptions: any[];
  placeholder?: string;
  onChange?: (val: any) => void;
  variant?:
    | "primarySolid"
    | "primaryOutline"
    | "secondarySolid"
    | "secondaryOutline";
};

type CSSProperties = React.CSSProperties | Record<string, React.CSSProperties>;

let indexPos: any;
export function Dropdown({
  id = "optionSearchInput",
  disabled = false,
  name,
  width,
  optionHeight,
  wrapperHeight,
  value,
  selectOptions,
  placeholder,
  onChange,
  variant = "primarySolid",
}: ZippyDropdownPropType) {
  const theme = useTheme<Theme>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = React.useState("");
  const [focusedOption, setFocusedOption] = React.useState<any>();

  // React.useEffect(() => {
  //   if (value) {
  //     setData(value.title)
  //   }
  // }, [value])

  React.useEffect(() => {
    if (value) {
      const selectedOption = selectOptions.find(
        (option) => option.value === value,
      );

      setData(selectedOption?.title || "");
    } else {
      setData("");
    }
  }, [value, selectOptions]);

  React.useEffect(() => {
    document.onkeydown = (e) => {
      if (e.key === "ArrowUp") {
        setFocusedOption(focusedOption - 1);
      } else if (e.key === "ArrowDown") {
        setFocusedOption(focusedOption + 1);
      }
    };
    scrolltoId(focusedOption);
  }, [focusedOption]);

  const scrollBarBackground = {
    primarySolid: theme.colors.scrollbar.primary,
    primaryOutline: theme.colors.scrollbar.primary,
    secondarySolid: theme.colors.scrollbar.secondary,
    secondaryOutline: theme.colors.scrollbar.secondary,
  };

  const wrapperBasestyle: CSSProperties = {
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
    paddingRight: "1rem",
    position: "relative",
    borderRadius: "4px",
    border: "1px solid",
    width: width ? width : "100%",
    minWidth: "100px",
  };
  const wrapperVariantPSolid: CSSProperties = {
    ...wrapperBasestyle,
    color: theme.colors.bg.main,
    backgroundColor: theme.colors.primary.main,
    borderColor: "none",
  };
  const wrapperVariantPOutline: CSSProperties = {
    ...wrapperBasestyle,
    color: theme.colors.primary.main,
    backgroundColor: "none",
    borderColor: disabled
      ? theme.colors.secondary.light
      : theme.colors.primary.main,
  };
  const wrapperVariantSSolid: CSSProperties = {
    ...wrapperBasestyle,
    color: theme.colors.bg.main,
    backgroundColor: theme.colors.secondary.main,
    borderColor: "none",
  };
  const wrapperVariantSOutline: CSSProperties = {
    ...wrapperBasestyle,
    color: theme.colors.secondary.main,
    backgroundColor: "none",
    borderColor: disabled
      ? theme.colors.secondary.light
      : theme.colors.secondary.main,
  };

  const optionsBasestyle: CSSProperties = {
    // position: "absolute",
    width: width ? width : "100%",
    borderRadius: "4px",
    border: "1px solid",
    height: optionHeight ? optionHeight : "max-content",
    overflowY: "scroll",
    boxSizing: "border-box",
    marginTop: "0.15rem",
    zIndex: 100,
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: scrollBarBackground[variant],
      height: "44px",
      borderRadius: "2px",
    },
  };
  const optionsVariantPSolid: CSSProperties = {
    ...optionsBasestyle,
    color: theme.colors.bg.main,
    backgroundColor: theme.colors.primary.main,
    borderColor: "inherit",
  };
  const optionsVariantPOutline = {
    ...optionsBasestyle,
    color: theme.colors.primary.main,
    backgroundColor: "white",
    borderColor: theme.colors.primary.main,
  };
  const optionsVariantSSolid: CSSProperties = {
    ...optionsBasestyle,
    color: theme.colors.bg.main,
    backgroundColor: theme.colors.secondary.main,
    borderColor: "inherit",
  };
  const optionsVariantSOutline = {
    ...optionsBasestyle,
    color: theme.colors.secondary.main,
    backgroundColor: "white",
    borderColor: theme.colors.secondary.main,
  };

  const wrapperStyle: CSSProperties = {
    ...(variant === "primarySolid" && wrapperVariantPSolid),
    ...(variant === "primaryOutline" && wrapperVariantPOutline),
    ...(variant === "secondarySolid" && wrapperVariantSSolid),
    ...(variant === "secondaryOutline" && wrapperVariantSOutline),
  };

  const optionsStyle: CSSProperties = {
    ...(variant === "primarySolid" && optionsVariantPSolid),
    ...(variant === "primaryOutline" && optionsVariantPOutline),
    ...(variant === "secondarySolid" && optionsVariantSSolid),
    ...(variant === "secondaryOutline" && optionsVariantSOutline),
  };

  function scrolltoId(id: any) {
    var option = document.getElementById(`option_${id}`) as HTMLElement;
    var wrapper = document.getElementById(`options_wrapper`) as HTMLElement;
    var topPos = option?.offsetTop - 10;
    wrapper.scrollTop = topPos;
  }

  function searchInput(e: string) {
    const pos = selectOptions
      .sort((a, b) => {
        if (a.value.toLowerCase() === b.value.toLowerCase()) return 0;
        return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
      })
      .filter((item, index) => {
        if (item.value.toLowerCase().indexOf(e.toLowerCase()) === 0) {
          indexPos = index;
        }
        return item.value.toLowerCase().indexOf(e.toLocaleLowerCase()) === 0;
      });
    if (indexPos && indexPos - pos.length === 0) {
      setFocusedOption(indexPos && indexPos - pos.length);
      return { index: indexPos && indexPos - pos.length };
    } else {
      setFocusedOption(indexPos && indexPos - pos.length + 1);
      return { index: indexPos && indexPos - pos.length + 1 };
    }
  }

  function initailizeOptionSearch() {
    var optionSearchInput = document.getElementById(id) as HTMLElement;
    optionSearchInput.focus();
    // setData("")
  }

  function handleInputChange(e: string) {
    var value = e.charAt(0).toUpperCase() + e.slice(1);
    onOpen();
    setData(value);
    const x = searchInput(value);
    setTimeout(() => {
      scrolltoId(x.index);
    }, 100);
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: "110px",
        borderRadius: "4px",
        position: "relative",
        // zIndex: 100,
        "&:focus-within": {
          // backgroundColor: theme.colors.bg.main
        },
      }}
    >
      <Flex sx={wrapperStyle}>
        <Box
          sx={{
            width: "80%",
            height: wrapperHeight ? wrapperHeight : "2.5rem",
          }}
        >
          <Input
            sx={{
              background: "transparent",
              border: "none",
              "&:focus-visible": { boxShadow: "none" },
            }}
            autoComplete="off"
            name={name}
            id={id}
            placeholder={placeholder}
            value={data}
            // onChange={(e) => handleInputChange(e.target.value)}
            isDisabled={disabled}
          />
        </Box>
        <Flex
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            width: "20%",
          }}
        >
          <Box>
            {!isOpen && (
              <MdOutlineKeyboardArrowDown
                size={24}
                onClick={() => {
                  if (!disabled) {
                    onOpen();
                    initailizeOptionSearch();
                  }
                }}
                cursor={disabled ? "not-allowed" : "pointer"}
              />
            )}
            {isOpen && (
              <MdOutlineKeyboardArrowUp
                size={24}
                onClick={() => {
                  if (!disabled) {
                    onClose();
                  }
                }}
                cursor={disabled ? "not-allowed" : "pointer"}
              />
            )}
          </Box>
        </Flex>
      </Flex>

      <Collapse in={isOpen} animateOpacity={true}>
        <Box id="options_wrapper" sx={optionsStyle}>
          {selectOptions
            .sort((a, b) => {
              if (a.value.toLowerCase() === b.value.toLowerCase()) return 0;
              return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
            })
            .map((item, index) => {
              return (
                <Flex
                  id={`option_${index}`}
                  key={index}
                  sx={{
                    alignItems: "center",
                    width: "100%",
                    backgroundColor:
                      index === focusedOption ? theme.colors.bg.main : "none",
                    color:
                      index === focusedOption
                        ? theme.colors.secondary.main
                        : "none",
                    "&:hover": {
                      backgroundColor: theme.colors.bg.paper,
                    },
                  }}
                >
                  <Text
                    sx={{
                      padding: "16px",
                      width: "100%",
                      textAlign: "left",
                      fontSize: theme.fontSizes.body,
                      fontWeight: 400,
                      fontFamily: theme.fonts.body,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setData(item.title);
                      onChange && onChange(item);
                      onClose();
                    }}
                  >
                    {item.title}
                  </Text>
                </Flex>
              );
            })}
        </Box>
      </Collapse>
    </Box>
  );
}
